import { useI18n } from "@lookiero/i18n-react";
import { Country, CURRENCY } from "@lookiero/sty-psp-locale";
import { AnswersProjection } from "@lookiero/sty-psp-quiz-style-profile-common-ui";
import { Segment } from "@lookiero/sty-psp-segment";
import { isUuid } from "@lookiero/sty-psp-uuid";
import {
  NextBestActionDefinitionAction,
  NextBestActionType,
} from "../../../../../projection/nextBestActionDefinition/nextBestActionDefinition";
import { nextBestActionDefinitionHasChildren } from "../../../../../projection/nextBestActionDefinition/nextBestActionDefinition.typeguards";
import { DOMAIN } from "../../../i18n/i18n";

const EMPTY_ANSWER = "-";

interface UseNextBestActionAnswersFunctionArgs {
  readonly action: NextBestActionDefinitionAction;
  readonly answers: AnswersProjection;
  readonly country: Country;
  readonly segment: Segment;
}

interface UseNextBestActionAnswersFunction {
  (args: UseNextBestActionAnswersFunctionArgs): string;
}

const findActionById = (
  actions: NextBestActionDefinitionAction[],
  actionId: string,
): NextBestActionDefinitionAction | undefined => {
  for (const action of actions) {
    if (action.id === actionId) {
      return action;
    }
    if (nextBestActionDefinitionHasChildren(action)) {
      const found = findActionById(action.children, actionId);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

const useNextBestActionAnswers: UseNextBestActionAnswersFunction = ({ action, answers, segment, country }) => {
  const { formatMessage, formatNumber } = useI18n({ domain: DOMAIN });

  const allAnswers = Object.entries(answers)
    .reduce((acc, [actionId, actionAnswers]) => {
      const currentAction =
        actionId === action.id
          ? action
          : nextBestActionDefinitionHasChildren(action)
            ? findActionById(action.children, actionId)
            : null;

      if (!currentAction) {
        return [...acc, EMPTY_ANSWER];
      }

      switch (currentAction.type) {
        case NextBestActionType.HOST_WEIGHT_IMPERIAL:
          if (!actionAnswers[0]) {
            return [...acc, EMPTY_ANSWER];
          }
          const [stones, pounds] = actionAnswers[0].split("/");
          return [...acc, `${stones} st ${pounds} lb`];

        case NextBestActionType.HOST_WEIGHT_METRIC:
          return [...acc, `${actionAnswers[0]} kg`];

        default:
          return [
            ...acc,
            ...actionAnswers.reduce((answersAcc, answer) => {
              if (!isUuid(answer)) {
                return [...answersAcc, answer];
              }

              if (!nextBestActionDefinitionHasChildren(action)) {
                return answersAcc;
              }

              const answerAction = findActionById(action.children, answer);

              if (!answerAction?.translationKey) {
                return answersAcc;
              }

              return answerAction.type === NextBestActionType.NUMBER_WITH_CURRENCY
                ? [
                    ...answersAcc,
                    formatNumber(Number(answerAction.translationKey), {
                      style: "currency",
                      currency: CURRENCY[country],
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                  ]
                : [
                    ...answersAcc,
                    formatMessage({ id: answerAction.translationKey }, { segment: segment.toLowerCase() }),
                  ];
            }, [] as string[]),
          ];
      }
    }, [] as string[])
    .join(", ");

  return allAnswers.length > 0 ? allAnswers : EMPTY_ANSWER;
};

export { useNextBestActionAnswers, EMPTY_ANSWER };
